import { useCallback, useRef, useState } from 'react'

/**
 * Hook to prevent double-execution of async handlers (e.g., button clicks).
 * Uses ref-based locking to guard against race conditions during fast re-renders.
 *
 * @template T - Return type of the async function
 * @param fn - Async function to wrap with execution protection
 * @returns Object with `run` function and `loading` state
 *
 * @example
 * ```tsx
 * const { run, loading } = useAsyncHandler(async () => {
 *   await saveData()
 * })
 *
 * <button onClick={run} disabled={loading}>
 *   {loading ? 'Saving...' : 'Save'}
 * </button>
 * ```
 */
export function useAsyncHandler<T = void>(
  fn: () => Promise<T>,
): {
  run: () => Promise<T | undefined>
  loading: boolean
} {
  const [loading, setLoading] = useState(false)
  const inFlightRef = useRef(false)

  const run = useCallback(async (): Promise<T | undefined> => {
    // Guard: prevent double execution using ref (survives re-renders)
    if (inFlightRef.current) {
      return undefined
    }

    inFlightRef.current = true
    setLoading(true)

    try {
      return await fn()
    } finally {
      inFlightRef.current = false
      setLoading(false)
    }
  }, [fn])

  return { run, loading }
}
